import { action, makeObservable, observable, runInAction } from 'mobx';

import { ENDPOINTS } from '@/configs/endpoints';
import {
  TREE_ROOT_NODE_ID,
  TreeNodeFolder,
  findTreeStoreByFolderId,
  isInsidePersonalWorkspace,
  treeNodeFolderSchema,
} from '@/entities/Tree';
import { notificationsStore } from '@/stores/global/RootStore';
import { MetaStore } from '@/stores/local/shared/MetaStore';
import { TreeStore } from '@/stores/local/shared/TreeStore';
import { FieldTextStore } from '@/stores/local/shared/form/FieldTextStore';
import { FormStore } from '@/stores/local/shared/form/FormStore';
import call from '@/utils/api/api';
import { getEmptyError, validateDateYyyyMmDd } from '@/utils/validators/validators';

type FieldsMap = {
  name: FieldTextStore;
};

type CreateFolderFormStoreParams = {
  getOrganizationId: () => number;
  treeStore: TreeStore;
  onSuccess: () => void;
};

type PrivateFields = '_folder' | '_parentId' | '_isDateFormatRequired';

export class CreateFolderFormStore extends FormStore<FieldsMap> {
  fieldsMap: FieldsMap;

  private readonly _getOrganizationId: () => number;
  private readonly _treeStore: TreeStore;
  private readonly _onSuccess: () => void;
  private _folder: TreeNodeFolder | null = null;
  private _parentId: string | null = null;
  private _isDateFormatRequired = false;
  private readonly _dateValidator = validateDateYyyyMmDd('Введите название в формате YYYY-MM-DD');

  readonly meta = new MetaStore();

  constructor({ getOrganizationId, treeStore, onSuccess }: CreateFolderFormStoreParams) {
    super();
    this._getOrganizationId = getOrganizationId;
    this._treeStore = treeStore;
    this._onSuccess = onSuccess;

    this.fieldsMap = {
      name: new FieldTextStore({
        required: true,
        validator: (value) => this._validateName(value ?? ''),
      }),
    };

    makeObservable<CreateFolderFormStore, PrivateFields>(this, {
      _folder: observable.ref,
      _parentId: observable.ref,
      _isDateFormatRequired: observable,
      setEntity: action.bound,
      setParentId: action.bound,
      clearContext: action.bound,
    });
  }

  async submit() {
    this.fieldsMap.name.touch();
    this.fieldsMap.name.validate();

    if (!this.validateFields()) {
      return;
    }

    const nameValue = (this.fieldsMap.name.serverFormat ?? '').trim();

    this.fieldsMap.name.setValue(nameValue, { noTouch: true });

    this.meta.setLoadedStartMeta();

    const isUpdate = Boolean(this._folder);

    const response = await call<TreeNodeFolder>({
      endpoint: isUpdate
        ? ENDPOINTS.folder.update(String(this._getOrganizationId()), String(this._folder?.id))
        : ENDPOINTS.folder.add(String(this._getOrganizationId())),
      method: isUpdate ? 'PUT' : 'POST',
      data: isUpdate
        ? {
            name: nameValue,
          }
        : {
            type: 'folder',
            name: nameValue,
            parent_id: this._parentId,
          },
    });

    if (response.isError) {
      const defaultText = this._folder ? 'Не удалось обновить папку' : 'Не удалось создать папку';

      notificationsStore.showError(response.error.response?.data.message ?? defaultText);
      this.meta.setLoadedErrorMeta();

      return;
    }

    const result = treeNodeFolderSchema.safeParse(response.data);

    if (!result.success) {
      notificationsStore.showError('Некорректный ответ от сервера');
      this.meta.setLoadedErrorMeta();

      return;
    }

    const folder = result.data;

    if (isUpdate) {
      this._treeStore.updateNode(folder);
    } else {
      this._treeStore.addNode(folder, this._parentId);
    }

    runInAction(() => {
      this._onSuccess();
      this.reset();
      this.meta.setLoadedSuccessMeta();
    });
  }

  setEntity(entity: TreeNodeFolder | null) {
    this._folder = entity;

    if (this._folder) {
      this.fieldsMap.name.setValue(this._folder.name);
    } else {
      this.reset();
    }
  }

  setParentId(parentId: string | null, options?: { requireDateFormat?: boolean }) {
    this._parentId = parentId;
    if (options && Object.prototype.hasOwnProperty.call(options, 'requireDateFormat')) {
      this._isDateFormatRequired = Boolean(options?.requireDateFormat);
    } else {
      this._isDateFormatRequired = this._shouldRequireDateFormat(parentId);
    }
  }

  clearContext() {
    this._folder = null;
    this._parentId = null;
    this._isDateFormatRequired = false;
  }

  get isDateFormatRequired(): boolean {
    return this._isDateFormatRequired;
  }

  get dateFormatPlaceholder(): string | undefined {
    return this._isDateFormatRequired ? 'Название папки в формате ГГГГ - ММ - ДД' : 'Новая папка';
  }

  get parentId(): string | null {
    return this._parentId;
  }

  private _validateName(value: string) {
    if (!this._isDateFormatRequired) {
      return getEmptyError();
    }

    return this._dateValidator(value ?? '');
  }

  private _shouldRequireDateFormat(parentId: string | null): boolean {
    if (!parentId || parentId === TREE_ROOT_NODE_ID) {
      return false;
    }

    const parentStore = findTreeStoreByFolderId(this._treeStore, parentId);

    if (parentStore && isInsidePersonalWorkspace(parentStore)) {
      return false;
    }

    return true;
  }

  destroy(): void {
    super.destroy();
    this.meta.destroy();
  }
}
